/**
 * Slot, option and value helpers shared by every block family.
 *
 * Layering (ADR-0092): a block prints slot values only. Where a value may
 * come from a Regta party or property, the slot says so with an `autofill`
 * binding that the framework's `resolveAutofill` resolves on top — auto by
 * default, switchable to manual text per field. Nothing here reads
 * `ctx.parties` or `ctx.properties`.
 *
 * Options: beta contracts store the ADR-0062 option `code`; legacy contracts
 * store the option's `value`. Every reader here accepts a code, a legacy
 * value or a label (the framework's `optionCodeFor` precedence), so one
 * compiler serves both lanes.
 */
import { optionCodeFor, val } from '@hyranai/regta-document-ir';
import { slotOptions } from '../lib.js';
/* ------------------------------------------------------------------ */
/* Autofill bindings                                                   */
/* ------------------------------------------------------------------ */
export const withAutofill = (slot, autofill) => ({ ...slot, autofill });
export const partyField = (role, ordinal, field) => ({
    kind: 'party',
    role,
    ordinal,
    field,
});
export const propertyField = (field, index) => ({
    kind: 'property',
    field,
    ...(index != null ? { index } : {}),
});
export const propertyListField = (field) => ({ kind: 'property_list', field });
/** Party fields whose value usually spans lines, so the editor offers a textarea. */
const MULTILINE_PARTY_FIELDS = new Set(['address']);
/** A slot printed from one party attribute, auto-filled by `(role, ordinal)`. */
export function partySlot(key, label, role, ordinal, field, extra = {}) {
    return withAutofill({ key, label, type: MULTILINE_PARTY_FIELDS.has(field) ? 'textarea' : 'text', ...extra }, partyField(role, ordinal, field));
}
/** A slot printed from one linked property's attribute (default: the first). */
export function propertySlot(key, label, field, extra = {}) {
    const { index, ...rest } = extra;
    return withAutofill({ key, label, type: 'text', ...rest }, propertyField(field, index));
}
/** A slot printed from an aggregate over every linked property. */
export function propertyListSlot(key, label, field, extra = {}) {
    return withAutofill({ key, label, type: 'textarea', ...extra }, propertyListField(field));
}
/** Options as slot options — `value` carried, so hosts translate both ways. */
export const toSlotOptions = (options) => slotOptions(options);
export const selectSlot = (key, label, options, extra = {}) => ({ key, label, type: 'select', options: toSlotOptions(options), ...extra });
/** The option a stored answer denotes (code, legacy value or label, in that order). */
export function pick(options, raw) {
    const code = optionCodeFor({ options: toSlotOptions(options) }, raw);
    return code === undefined ? undefined : options.find((o) => o.code === code);
}
/** Every option a multi-select answer denotes, in answer order; unknown entries drop. */
export function picks(options, raw) {
    if (!Array.isArray(raw))
        return [];
    return raw.flatMap((entry) => {
        const found = pick(options, entry);
        return found ? [found] : [];
    });
}
/** The code of a stored answer, or `null` (free text, unset). */
export const pickCode = (options, raw) => pick(options, raw)?.code ?? null;
/**
 * Whether the answer denotes `option` — for a select, the chosen option;
 * for a multi-select, one of the chosen ones. Takes the option or its code.
 */
export function optionChosen(options, raw, option) {
    const code = typeof option === 'string' ? option : option.code;
    return Array.isArray(raw) ? picks(options, raw).some((o) => o.code === code) : pick(options, raw)?.code === code;
}
const spellings = (option) => option.value === undefined || option.value === option.code ? [option.code] : [option.code, option.value];
/**
 * Visibility / `includeWhen` on a select holding any of these options.
 * Matches the code and the legacy value, because a composition's
 * `includeWhen` is evaluated literally (no slot table to canonicalise by).
 */
export function whenOption(key, ...options) {
    const accepted = [...new Set(options.flatMap(spellings))];
    return accepted.length === 1 ? { key, equals: accepted[0] } : { key, oneOf: accepted };
}
/** Visibility on a multi-select including any of these options (code or legacy value). */
export function whenIncludes(key, ...options) {
    const conditions = [...new Set(options.flatMap(spellings))].map((v) => ({ key, includes: v }));
    return conditions.length === 1 ? conditions[0] : { any: conditions };
}
/** What the legacy lane printed for an option: its render label, else its stored value, else its label. */
export const optionPrinted = (option) => option.renderLabel ?? option.value ?? option.label;
/** Printed text of a select answer; unknown answers (free text) print as typed. */
export function optionText(options, raw) {
    const option = pick(options, raw);
    return option ? optionPrinted(option) : slotText(raw);
}
/** A select answer as a value node: the printed text, with the option code as `raw`. */
export function optionVal(options, input, key, blankWidth = 'medium') {
    const option = pick(options, input[key]);
    return val(option ? optionPrinted(option) : slotText(input[key]), {
        ...(option ? { raw: option.code } : {}),
        sourceKey: key,
        blankWidth,
    });
}
/* ------------------------------------------------------------------ */
/* Values                                                              */
/* ------------------------------------------------------------------ */
/**
 * The legacy `v()` normalisation: runs of spaces collapse, line breaks
 * survive (the renderer prints them pre-wrap), the ends are trimmed.
 * Empty ⇒ `null`, which the renderer prints as the fill-in placeholder.
 */
export function slotText(raw) {
    if (raw === null || raw === undefined)
        return null;
    const normalized = String(raw)
        .replace(/[^\S\n]+/g, ' ')
        .replace(/[ \t]*\n[ \t]*/g, '\n')
        .trim();
    return normalized === '' ? null : normalized;
}
/** A finite number from a number or numeric string, else `null`. */
export function slotNumber(raw) {
    if (typeof raw === 'number')
        return Number.isFinite(raw) ? raw : null;
    if (typeof raw === 'string' && raw.trim() !== '') {
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
/** Legacy truthiness of a gate value: `true`, non-empty text, a number, a non-empty list. */
export function isFilled(raw) {
    if (raw === true)
        return true;
    if (typeof raw === 'number')
        return Number.isFinite(raw);
    if (typeof raw === 'string')
        return raw.trim() !== '';
    if (Array.isArray(raw))
        return raw.length > 0;
    return false;
}
/** A text slot as a value node (legacy `v(values[key])`). */
export const textVal = (input, key, blankWidth = 'medium') => val(slotText(input[key]), { sourceKey: key, blankWidth });
/** Legacy `num()`: the locale's grouping, the value's own decimals (max three). */
export function formatSlotNumber(ctx, raw) {
    const n = slotNumber(raw);
    return n == null ? null : new Intl.NumberFormat(ctx.locale, { maximumFractionDigits: 3 }).format(n);
}
export function numVal(ctx, input, key, opts = {}) {
    const n = slotNumber(input[key]);
    const text = formatSlotNumber(ctx, n);
    return val(text == null ? null : `${text}${opts.suffix ?? ''}`, {
        raw: n,
        format: 'number',
        sourceKey: key,
        blankWidth: opts.blankWidth ?? 'short',
    });
}
export const dateVal = (ctx, input, key, blankWidth = 'medium') => val(ctx.format.date(slotText(input[key])), { format: 'date', sourceKey: key, blankWidth });
/** Legacy visibility rule: a blank form shows every optional part. */
export const shown = (ctx, predicate) => ctx.mode === 'blank' || predicate;
/** Legacy tick rule: a blank form leaves every box unticked. */
export const ticked = (ctx, predicate) => ctx.mode !== 'blank' && predicate;
//# sourceMappingURL=slots.js.map